import { Modal } from './Modal';
import { Button } from './Button';
import { ImageWithFallback } from './ImageWithFallback';
import { QuantityStepper } from './QuantityStepper';
import { StoreProductDetail } from '../../models';
import { formatCurrency } from '../../utils/currency';
import { safeImageUrl } from '../../utils/url';
import { getVariationLabel } from '../../utils/variantResolution';
import { useVariantSelection } from '../../hooks/useVariantSelection';

interface QuickAddModalProps {
  product: StoreProductDetail | null;
  onClose: () => void;
}

export function QuickAddModal({ product, onClose }: QuickAddModalProps) {
  const {
    hasVariants,
    uniqueColors,
    uniqueValues,
    selectedColor,
    selectedValue,
    quantity,
    resolvedVariant,
    stockQuantity,
    isOutOfStock,
    canAddToCart,
    selectColor,
    selectValue,
    increaseQuantity,
    decreaseQuantity,
    addToCart,
  } = useVariantSelection(product);

  const handleAdd = () => {
    if (addToCart()) onClose();
  };

  const displayPrice = product ? product.promotionalPrice ?? product.price : null;
  const thumbnail = product?.images[0]?.imageUrl;

  return (
    <Modal open={!!product} onClose={onClose} title="Adicionar à sacola">
      {product && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <ImageWithFallback
              src={thumbnail ? safeImageUrl(thumbnail) : undefined}
              alt={product.name}
              className="w-14 h-14 rounded-lg flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="font-display font-semibold text-text-dark text-sm leading-snug truncate">
                {product.name}
              </p>
              {displayPrice !== null && displayPrice !== undefined && (
                <p className="text-chocolate font-bold text-sm mt-0.5">R$ {formatCurrency(displayPrice)}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {uniqueColors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-text-medium mb-2">
                  Cor{selectedColor && <span className="text-text-light font-medium"> — {selectedColor.name}</span>}
                </p>
                <div className="flex flex-wrap gap-2">
                  {uniqueColors.map((color) => {
                    const outOfStock = !product.variants.some(
                      (v) => v.stockQuantity > 0 && v.color?.id === color.id
                    );
                    return (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => !outOfStock && selectColor(color)}
                        disabled={outOfStock}
                        title={outOfStock ? `${color.name} (sem estoque)` : color.name}
                        aria-label={color.name}
                        aria-pressed={selectedColor?.id === color.id}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          outOfStock
                            ? 'opacity-50 cursor-not-allowed border-champagne-dark'
                            : selectedColor?.id === color.id
                            ? 'border-chocolate scale-110 shadow-md'
                            : 'border-champagne-dark hover:border-chocolate/60 hover:scale-105'
                        }`}
                        style={{
                          backgroundColor: color.hexadecimal,
                          backgroundImage: outOfStock
                            ? 'linear-gradient(to top right, transparent calc(50% - 1px), rgba(0,0,0,0.6) calc(50% - 1px), rgba(0,0,0,0.6) calc(50% + 1px), transparent calc(50% + 1px))'
                            : undefined,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {hasVariants && uniqueValues.length > 0 && (
              <div>
                <p className="text-sm font-medium text-text-medium mb-2">
                  {getVariationLabel(product.variants[0]?.variationTypeId)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {uniqueValues.map((val) => {
                    const variant = product.variants.find(
                      (v) => v.variationValue === val && (!selectedColor || v.color?.id === selectedColor.id)
                    );
                    const outOfStock = variant ? variant.stockQuantity === 0 : false;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => !outOfStock && selectValue(val)}
                        disabled={outOfStock}
                        title={variant?.label}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                          outOfStock
                            ? 'border-champagne-dark text-text-light opacity-50 cursor-not-allowed line-through'
                            : selectedValue === val
                            ? 'bg-chocolate text-white border-chocolate'
                            : 'border-champagne-dark text-text-medium hover:border-chocolate'
                        }`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(!hasVariants || resolvedVariant) && (
              <div>
                <QuantityStepper
                  quantity={quantity}
                  onDecrease={decreaseQuantity}
                  onIncrease={increaseQuantity}
                  disableDecrease={quantity <= 1}
                  disableIncrease={hasVariants && quantity >= stockQuantity}
                />
              </div>
            )}

            <Button fullWidth variant="primary" onClick={handleAdd} disabled={!canAddToCart}>
              {isOutOfStock ? 'Esgotado' : 'Adicionar à sacola'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
